import { useEffect, useState, type FormEvent } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Button, Field, Input, PageHead, useToast } from '@w7/ui'
import { sessionApi } from '@/api/sessionApi'
import { getUserProfile } from '@/auth/session'

type SubMarketInfoForm = {
  name: string
  symbol: string
  source: string
  rate_diff: string | number
  market_starttime: string
  market_endtime: string
  market_resulttime: string
  status: string
  order: string | number
  _id?: string
  [key: string]: unknown
}

const emptyForm = (): SubMarketInfoForm => ({
  name: '',
  symbol: '',
  source: '',
  rate_diff: '',
  market_starttime: '00:00',
  market_endtime: '00:00',
  market_resulttime: '00:00',
  status: '',
  order: '',
})

function normalizeTime(value: unknown): string {
  if (value == null || value === '') return '00:00'
  if (typeof value === 'string') {
    // vue-timepicker may store "HH:mm" or object-serialized strings
    const m = value.match(/^(\d{1,2}):(\d{2})/)
    if (m) return `${m[1]!.padStart(2, '0')}:${m[2]}`
    return value
  }
  if (typeof value === 'object' && value !== null) {
    const o = value as { HH?: string; mm?: string; hours?: string; minutes?: string }
    const hh = String(o.HH ?? o.hours ?? '00').padStart(2, '0')
    const mm = String(o.mm ?? o.minutes ?? '00').padStart(2, '0')
    return `${hh}:${mm}`
  }
  return String(value)
}

export function CreateSubMarketInfoPage() {
  const toast = useToast()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const id = params.get('id')
  const [form, setForm] = useState<SubMarketInfoForm>(emptyForm)

  useEffect(() => {
    void getUserProfile().then((u) => {
      if (u?.accessLevel === 'user') toast.error('Access Denied')
    })
  }, [toast])

  useEffect(() => {
    if (!id) return
    void sessionApi
      .getOneSubmarketInfo({ subMarketInfoId: id })
      .then((res) => {
        const data = res.data as SubMarketInfoForm
        if (!data) return
        setForm({
          ...data,
          market_starttime: normalizeTime(data.market_starttime),
          market_endtime: normalizeTime(data.market_endtime),
          market_resulttime: normalizeTime(data.market_resulttime),
          rate_diff: data.rate_diff ?? '',
          order: data.order ?? '',
          source: String(data.source ?? ''),
          status: String(data.status ?? ''),
        })
      })
      .catch((err: { response?: { data?: { message?: string } } }) => {
        toast.error(err?.response?.data?.message ?? 'Failed to load')
      })
  }, [id, toast])

  const incomplete =
    !form.status ||
    !form.name ||
    !form.symbol ||
    !form.source ||
    !form.rate_diff ||
    !form.market_starttime ||
    !form.market_endtime ||
    !form.market_resulttime ||
    !form.order

  const setField = <K extends keyof SubMarketInfoForm>(key: K, value: SubMarketInfoForm[K]) => {
    setForm((f) => ({ ...f, [key]: value }))
  }

  const saveCreate = async (e: FormEvent) => {
    e.preventDefault()
    if (id || incomplete) return
    try {
      const res = await sessionApi.saveSubmarketInfo({ ...form })
      if (res.data && res.status === 200) {
        toast.success('SubMarketInfo Save Successfully')
        navigate('/submarketinfo')
      } else {
        toast.error('Something Went Wrong')
      }
    } catch {
      toast.error('Something Went Wrong')
    }
  }

  const saveEdit = async () => {
    if (!id || incomplete) return
    try {
      const res = await sessionApi.updateSubMarketInfo({ ...form })
      if (res.data && res.status === 200) {
        toast.success('SubMarketInfo Updated Successfully')
        navigate('/submarketinfo')
      } else {
        toast.error('Something Went Wrong')
      }
    } catch (err: unknown) {
      toast.error(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          'Something Went Wrong',
      )
    }
  }

  const field = (
    label: string,
    key: keyof SubMarketInfoForm,
    opts?: { type?: string; required?: boolean; placeholder?: string },
  ) => (
    <Field label={label} required={opts?.required !== false}>
      {({ id: fid, describedBy, invalid }) => (
        <Input
          id={fid}
          aria-describedby={describedBy}
          invalid={invalid}
          type={opts?.type ?? 'text'}
          placeholder={opts?.placeholder ?? label}
          value={String(form[key] ?? '')}
          onChange={(e) => setField(key, e.target.value)}
          required={opts?.required !== false}
        />
      )}
    </Field>
  )

  return (
    <div>
      <PageHead
        title={id ? 'Edit Sub Market Info' : 'Create Sub Market Info'}
        onBack={() => navigate('/submarketinfo')}
      />
      <form
        className="sm-card"
        style={{ maxWidth: 560, display: 'flex', flexDirection: 'column', gap: 12 }}
        onSubmit={(e) => void saveCreate(e)}
      >
        {field('Name', 'name', { placeholder: 'Name' })}
        {field('Symbol', 'symbol', { placeholder: 'Symbol' })}
        {field('Source', 'source', { placeholder: 'source' })}
        {field('Rate-Diff', 'rate_diff', { type: 'number', placeholder: 'Rate-Diff' })}
        {field('startTime', 'market_starttime', { type: 'time' })}
        {field('End Time', 'market_endtime', { type: 'time' })}
        {field('Result Time', 'market_resulttime', { type: 'time' })}
        <Field label="Status" required>
          {({ id: fid }) => (
            <select
              id={fid}
              className="w7-input"
              value={form.status}
              onChange={(e) => setField('status', e.target.value)}
              required
            >
              <option value="">Select status</option>
              <option value="Enable">Enable</option>
              <option value="Disable">Disable</option>
            </select>
          )}
        </Field>
        {field('Order', 'order', { type: 'number', placeholder: 'Order' })}

        {!id ? (
          <Button type="submit" variant="primary" disabled={incomplete} style={{ marginTop: 8 }}>
            Create
          </Button>
        ) : (
          <div className="sm-row" style={{ marginTop: 8 }}>
            <Button type="button" onClick={() => navigate('/submarketinfo')}>
              Cancel
            </Button>
            <Button type="button" variant="primary" disabled={incomplete} onClick={() => void saveEdit()}>
              Edit
            </Button>
          </div>
        )}
      </form>
    </div>
  )
}
